// src/pages/Admin/EmployeeOffer/EmployeeOfferManagement.tsx
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Badge, Button, Col, Container, Form,
  InputGroup, Modal, Nav, Pagination, Row, Spinner, Table,
} from 'react-bootstrap';
import {
  FaBan, FaBriefcase, FaCheck, FaClipboardList, FaExclamationTriangle,
  FaHardHat, FaHistory, FaInbox, FaPen, FaSearch,
  FaTimesCircle, FaUndo, FaUserCheck,
} from 'react-icons/fa';
import {
  approveSynchRecord, deactivateOffer, dismissSynchRecord,
  EmployeeOfferDashboard, EmployeeOfferRecord, EmployeeOfferSynchRecord,
  getDashboard, getImportLogs, getOffers, getSynchQueue, getReportRuns,
  ImportLog, PagedResult, reactivateOffer, SyncReportRun, updateBusinessAgreement,
} from '@/api/employeeOfferApi';
import './EmployeeOfferManagement.scss';

const PAGE_SIZE = 25;

// ─── Helpers ──────────────────────────────────────────────────────────────────

const fmt = (d?: string) => d ? new Date(d).toLocaleString() : '—';
const fmtDate = (d?: string) => d ? new Date(d).toLocaleDateString() : '—';
const daysAgo = (d: string) => Math.floor((Date.now() - new Date(d).getTime()) / 86_400_000);

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    '1': 'success', '0': 'secondary',
    ContractorReview: 'warning', NotFound: 'danger',
    Pending: 'primary', Approved: 'success', Dismissed: 'secondary',
  };
  const labels: Record<string, string> = {
    '1': 'Active', '0': 'Inactive',
    ContractorReview: 'Contractor Review', NotFound: 'Not Found',
    Approved: 'Approved', Dismissed: 'Dismissed', Pending: 'Pending',
  };
  return <Badge bg={map[status] ?? 'secondary'}>{labels[status] ?? status}</Badge>;
}

function KpiCard({
  label, value, variant, icon, onClick,
}: {
  label: string; value: number; variant: string; icon: React.ReactNode; onClick?: () => void;
}) {
  return (
    <Col xs={12} sm={6} lg={true}>
      <div
        className={`eo-kpi-card eo-kpi-card--${variant}${onClick ? ' eo-kpi-card--clickable' : ''}`}
        onClick={onClick}
        role={onClick ? 'button' : undefined}
        tabIndex={onClick ? 0 : undefined}
        onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') onClick(); } : undefined}
      >
        <div className="eo-kpi-icon">{icon}</div>
        <div className="eo-kpi-value">{value.toLocaleString()}</div>
        <div className="eo-kpi-label">{label}</div>
      </div>
    </Col>
  );
}

function PaginationBar({
  page, pageSize, total, onChange,
}: {
  page: number; pageSize: number; total: number; onChange: (p: number) => void;
}) {
  const totalPages = Math.ceil(total / pageSize);
  if (totalPages <= 1) return null;
  return (
    <div className="d-flex justify-content-between align-items-center mt-3">
      <small className="text-muted">
        {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, total)} of {total}
      </small>
      <Pagination size="sm" className="mb-0">
        <Pagination.Prev disabled={page === 1} onClick={() => onChange(page - 1)} />
        {page > 2 && <Pagination.Item onClick={() => onChange(1)}>1</Pagination.Item>}
        {page > 3 && <Pagination.Ellipsis />}
        {page > 1 && <Pagination.Item onClick={() => onChange(page - 1)}>{page - 1}</Pagination.Item>}
        <Pagination.Item active>{page}</Pagination.Item>
        {page < totalPages && <Pagination.Item onClick={() => onChange(page + 1)}>{page + 1}</Pagination.Item>}
        {page < totalPages - 2 && <Pagination.Ellipsis />}
        {page < totalPages - 1 && <Pagination.Item onClick={() => onChange(totalPages)}>{totalPages}</Pagination.Item>}
        <Pagination.Next disabled={page === totalPages} onClick={() => onChange(page + 1)} />
      </Pagination>
    </div>
  );
}

// ─── Overview Tab ─────────────────────────────────────────────────────────────

function OverviewTab({ dashboard, loading }: { dashboard: EmployeeOfferDashboard | null; loading: boolean }) {
  if (loading) return <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>;
  if (!dashboard) return null;

  return (
    <div className="eo-overview">
      <Row className="g-3 mb-4">
        <KpiCard label="Active Offers"        value={dashboard.activeOffers}        variant="success" icon={<FaUserCheck />} />
        <KpiCard label="Pending Sync"         value={dashboard.pendingSync}         variant="primary" icon={<FaInbox />} />
        <KpiCard label="Contractor Review"    value={dashboard.contractorReview}    variant="warning" icon={<FaHardHat />} />
        <KpiCard label="Not Found"            value={dashboard.notFound}            variant="danger"  icon={<FaExclamationTriangle />} />
        <KpiCard label="Deactivated (30 days)" value={dashboard.deactivatedThisMonth} variant="muted" icon={<FaTimesCircle />} />
      </Row>

      <div className="eo-overview-notes">
        <h6 className="text-muted mb-2">How the sync works</h6>
        <ul className="text-muted small">
          <li>Runs automatically <strong>twice a month</strong> via a scheduled Azure Function.</li>
          <li>Re-checks all active offers against the employee hierarchy — deactivates leavers automatically.</li>
          <li>Processes staging records from <em>Sync Queue</em>; matches by email or name.</li>
          <li><strong>Contractor Review</strong> records were matched but require manual approval before import.</li>
          <li><strong>Not Found</strong> records had no matching entry in the employee hierarchy.</li>
        </ul>
      </div>
    </div>
  );
}

// ─── Active Offers Tab ────────────────────────────────────────────────────────

function ActiveOffersTab() {
  const [data, setData] = useState<PagedResult<EmployeeOfferRecord> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);

  // Edit modal
  const [editRecord, setEditRecord] = useState<EmployeeOfferRecord | null>(null);
  const [editValue, setEditValue] = useState('');
  const [editSaving, setEditSaving] = useState(false);

  // Deactivate modal
  const [deactivateRecord, setDeactivateRecord] = useState<EmployeeOfferRecord | null>(null);
  const [deactivating, setDeactivating] = useState(false);

  // Reactivate modal
  const [reactivateRecord, setReactivateRecord] = useState<EmployeeOfferRecord | null>(null);
  const [reactivating, setReactivating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getOffers(page, PAGE_SIZE, search, includeInactive);
      setData(result);
    } catch {
      setError('Failed to load offers');
    } finally {
      setLoading(false);
    }
  }, [page, search, includeInactive]);

  useEffect(() => { load(); }, [load]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput);
  };

  const handleEditSave = async () => {
    if (!editRecord || !editValue.trim()) return;
    setEditSaving(true);
    try {
      await updateBusinessAgreement(editRecord.employeeOfferId, editValue.trim());
      setEditRecord(null);
      await load();
    } catch {
      setError('Failed to update business agreement');
    } finally {
      setEditSaving(false);
    }
  };

  const handleDeactivate = async () => {
    if (!deactivateRecord) return;
    setDeactivating(true);
    try {
      await deactivateOffer(deactivateRecord.employeeOfferId);
      setDeactivateRecord(null);
      await load();
    } catch {
      setError('Failed to deactivate offer');
    } finally {
      setDeactivating(false);
    }
  };

  const handleReactivate = async () => {
    if (!reactivateRecord) return;
    setReactivating(true);
    try {
      await reactivateOffer(reactivateRecord.employeeOfferId);
      setReactivateRecord(null);
      await load();
    } catch {
      setError('Failed to reactivate offer');
    } finally {
      setReactivating(false);
    }
  };

  return (
    <div>
      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}

      <div className="d-flex flex-wrap gap-2 mb-3 align-items-center">
        <Form onSubmit={handleSearch} className="d-flex gap-2 flex-grow-1">
          <InputGroup style={{ maxWidth: 380 }}>
            <Form.Control
              placeholder="Search name, email, ID, or account…"
              value={searchInput}
              onChange={e => setSearchInput(e.target.value)}
            />
            <Button variant="outline-secondary" type="submit"><FaSearch /></Button>
          </InputGroup>
        </Form>
        <Form.Check
          type="switch"
          label="Show deactivated"
          checked={includeInactive}
          onChange={e => { setIncludeInactive(e.target.checked); setPage(1); }}
        />
      </div>

      {loading ? (
        <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
      ) : (
        <>
          <div className="table-responsive">
            <Table hover size="sm" className="eo-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Employee ID</th>
                  <th>Business Agreement</th>
                  <th>Start Date</th>
                  <th>Status</th>
                  <th>Last Checked</th>
                  <th style={{ width: 120 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {data?.items.map(r => (
                  <tr key={r.employeeOfferId}>
                    <td>{r.employeeFirstName} {r.employeeLastName}</td>
                    <td className="text-truncate" style={{ maxWidth: 200 }}>{r.employeeEmail}</td>
                    <td>{r.employeeId}</td>
                    <td>{r.businessAgreement}</td>
                    <td>{fmtDate(r.employeeStartDate)}</td>
                    <td><StatusBadge status={r.employeeStatus} /></td>
                    <td>
                      <span className={daysAgo(r.employeeStatusLastChecked) > 45 ? 'text-warning' : ''}>
                        {fmtDate(r.employeeStatusLastChecked)}
                      </span>
                    </td>
                    <td>
                      <div className="d-flex gap-1">
                        <Button
                          size="sm" variant="outline-secondary" title="Edit business agreement"
                          onClick={() => { setEditRecord(r); setEditValue(r.businessAgreement); }}
                        >
                          <FaPen />
                        </Button>
                        {r.effEndDt ? (
                          <Button size="sm" variant="outline-success" title="Reactivate"
                            onClick={() => setReactivateRecord(r)}>
                            <FaUndo />
                          </Button>
                        ) : (
                          <Button size="sm" variant="outline-danger" title="Deactivate"
                            onClick={() => setDeactivateRecord(r)}>
                            <FaTimesCircle />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {data?.items.length === 0 && (
                  <tr><td colSpan={8} className="text-center text-muted py-4">No records found</td></tr>
                )}
              </tbody>
            </Table>
          </div>
          {data && <PaginationBar page={page} pageSize={PAGE_SIZE} total={data.totalCount} onChange={setPage} />}
        </>
      )}

      {/* Edit Modal */}
      <Modal show={!!editRecord} onHide={() => setEditRecord(null)}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Business Agreement</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {editRecord && (
            <>
              <p className="text-muted mb-3">
                <strong>{editRecord.employeeFirstName} {editRecord.employeeLastName}</strong>{' '}
                &mdash; {editRecord.employeeEmail}
              </p>
              <Form.Group>
                <Form.Label>Business Agreement (Account Number)</Form.Label>
                <Form.Control
                  value={editValue}
                  onChange={e => setEditValue(e.target.value)}
                  maxLength={128}
                  autoFocus
                />
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setEditRecord(null)}>Cancel</Button>
          <Button variant="primary" onClick={handleEditSave} disabled={editSaving}>
            {editSaving ? <Spinner size="sm" animation="border" /> : 'Save'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Deactivate Confirm */}
      <Modal show={!!deactivateRecord} onHide={() => setDeactivateRecord(null)}>
        <Modal.Header closeButton>
          <Modal.Title>Deactivate Offer</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to deactivate the offer for{' '}
          <strong>{deactivateRecord?.employeeFirstName} {deactivateRecord?.employeeLastName}</strong>?
          <p className="text-muted mt-2 mb-0 small">This is reversible — you can reactivate it later.</p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDeactivateRecord(null)}>Cancel</Button>
          <Button variant="danger" onClick={handleDeactivate} disabled={deactivating}>
            {deactivating ? <Spinner size="sm" animation="border" /> : 'Deactivate'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Reactivate Confirm */}
      <Modal show={!!reactivateRecord} onHide={() => setReactivateRecord(null)}>
        <Modal.Header closeButton>
          <Modal.Title>Reactivate Offer</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Reactivate the offer for{' '}
          <strong>{reactivateRecord?.employeeFirstName} {reactivateRecord?.employeeLastName}</strong>?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setReactivateRecord(null)}>Cancel</Button>
          <Button variant="success" onClick={handleReactivate} disabled={reactivating}>
            {reactivating ? <Spinner size="sm" animation="border" /> : 'Reactivate'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

// ─── Sync Queue Tab ───────────────────────────────────────────────────────────

function SynchQueueTab({ reviewStatus }: { reviewStatus: string }) {
  const [data, setData] = useState<PagedResult<EmployeeOfferSynchRecord> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  // Action modals
  const [approveRecord, setApproveRecord] = useState<EmployeeOfferSynchRecord | null>(null);
  const [approving, setApproving] = useState(false);
  const [dismissRecord, setDismissRecord] = useState<EmployeeOfferSynchRecord | null>(null);
  const [dismissing, setDismissing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getSynchQueue(page, PAGE_SIZE, reviewStatus || undefined);
      setData(result);
    } catch {
      setError('Failed to load sync queue');
    } finally {
      setLoading(false);
    }
  }, [page, reviewStatus]);

  useEffect(() => { setPage(1); }, [reviewStatus]);
  useEffect(() => { load(); }, [load]);

  const handleApprove = async () => {
    if (!approveRecord) return;
    setApproving(true);
    try {
      await approveSynchRecord(approveRecord.employeeOfferSynchId);
      setApproveRecord(null);
      await load();
    } catch {
      setError('Failed to approve record');
    } finally {
      setApproving(false);
    }
  };

  const handleDismiss = async () => {
    if (!dismissRecord) return;
    setDismissing(true);
    try {
      await dismissSynchRecord(dismissRecord.employeeOfferSynchId);
      setDismissRecord(null);
      await load();
    } catch {
      setError('Failed to dismiss record');
    } finally {
      setDismissing(false);
    }
  };

  const isContractorView = reviewStatus === 'ContractorReview';

  return (
    <div>
      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}

      {loading ? (
        <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
      ) : (
        <>
          <div className="table-responsive">
            <Table hover size="sm" className="eo-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Business Agreement</th>
                  {isContractorView && <th>Position Title</th>}
                  <th>Status</th>
                  <th>Submitted</th>
                  <th>Days Waiting</th>
                  <th style={{ width: isContractorView ? 160 : 90 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {data?.items.map(r => (
                  <tr key={r.employeeOfferSynchId}>
                    <td>{r.employeeFirstName} {r.employeeLastName}</td>
                    <td className="text-truncate" style={{ maxWidth: 200 }}>{r.employeeEmail}</td>
                    <td>{r.businessAgreement ?? '—'}</td>
                    {isContractorView && <td>{r.positionTitle ?? '—'}</td>}
                    <td>
                      {r.reviewStatus
                        ? <StatusBadge status={r.reviewStatus} />
                        : <Badge bg="primary">Pending</Badge>}
                    </td>
                    <td>{fmtDate(r.createDt)}</td>
                    <td>
                      <span className={daysAgo(r.createDt) > 20 ? 'text-warning fw-semibold' : ''}>
                        {daysAgo(r.createDt)}d
                      </span>
                    </td>
                    <td>
                      <div className="d-flex gap-1">
                        {isContractorView && (
                          <Button size="sm" variant="outline-success" title="Approve — import to active offers"
                            onClick={() => setApproveRecord(r)}>
                            <FaCheck />
                          </Button>
                        )}
                        {!r.reviewStatus || r.reviewStatus === 'ContractorReview' || r.reviewStatus === 'NotFound' ? (
                          <Button size="sm" variant="outline-danger" title="Dismiss"
                            onClick={() => setDismissRecord(r)}>
                            <FaTimesCircle />
                          </Button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
                {data?.items.length === 0 && (
                  <tr><td colSpan={isContractorView ? 8 : 7} className="text-center text-muted py-4">
                    No records found
                  </td></tr>
                )}
              </tbody>
            </Table>
          </div>
          {data && <PaginationBar page={page} pageSize={PAGE_SIZE} total={data.totalCount} onChange={setPage} />}
        </>
      )}

      {/* Approve Confirm */}
      <Modal show={!!approveRecord} onHide={() => setApproveRecord(null)}>
        <Modal.Header closeButton>
          <Modal.Title>Approve Contractor</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Approve and import the offer for{' '}
            <strong>{approveRecord?.employeeFirstName} {approveRecord?.employeeLastName}</strong>?
          </p>
          {approveRecord?.positionTitle && (
            <p className="text-muted small mb-1">
              Position: <strong>{approveRecord.positionTitle}</strong>
            </p>
          )}
          <p className="text-muted small mb-0">
            This will create or update an active offer record and mark the staging record as imported.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setApproveRecord(null)}>Cancel</Button>
          <Button variant="success" onClick={handleApprove} disabled={approving}>
            {approving ? <Spinner size="sm" animation="border" /> : <><FaCheck className="me-1" />Approve</>}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Dismiss Confirm */}
      <Modal show={!!dismissRecord} onHide={() => setDismissRecord(null)}>
        <Modal.Header closeButton>
          <Modal.Title>Dismiss Record</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Dismiss the staging record for{' '}
          <strong>{dismissRecord?.employeeFirstName} {dismissRecord?.employeeLastName}</strong>?
          <p className="text-muted mt-2 mb-0 small">
            The record will be soft-deleted and excluded from future sync processing.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setDismissRecord(null)}>Cancel</Button>
          <Button variant="danger" onClick={handleDismiss} disabled={dismissing}>
            {dismissing ? <Spinner size="sm" animation="border" /> : 'Dismiss'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

// ─── Import Logs Tab ──────────────────────────────────────────────────────────

function ImportLogsTab() {
  const [data, setData] = useState<PagedResult<ImportLog> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getImportLogs(page, PAGE_SIZE);
      setData(result);
    } catch {
      setError('Failed to load import logs');
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}

      {loading ? (
        <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
      ) : (
        <>
          <div className="table-responsive">
            <Table hover size="sm" className="eo-table eo-logs-table">
              <thead>
                <tr>
                  <th style={{ width: 180 }}>Timestamp</th>
                  <th>Message</th>
                </tr>
              </thead>
              <tbody>
                {data?.items.map(l => (
                  <tr key={l.importLogId}>
                    <td className="text-muted small text-nowrap">{fmt(l.createDt)}</td>
                    <td className="small">{l.description}</td>
                  </tr>
                ))}
                {data?.items.length === 0 && (
                  <tr><td colSpan={2} className="text-center text-muted py-4">No log entries found</td></tr>
                )}
              </tbody>
            </Table>
          </div>
          {data && <PaginationBar page={page} pageSize={PAGE_SIZE} total={data.totalCount} onChange={setPage} />}
        </>
      )}
    </div>
  );
}

// ─── Deactivated Offers Tab ──────────────────────────────────────────────────

function DeactivatedOffersTab() {
  const [data, setData] = useState<PagedResult<EmployeeOfferRecord> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');

  // Reactivate modal
  const [reactivateRecord, setReactivateRecord] = useState<EmployeeOfferRecord | null>(null);
  const [reactivating, setReactivating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getOffers(page, PAGE_SIZE, search, false, true);
      setData(result);
    } catch {
      setError('Failed to load deactivated offers');
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => { load(); }, [load]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput);
  };

  const handleReactivate = async () => {
    if (!reactivateRecord) return;
    setReactivating(true);
    try {
      await reactivateOffer(reactivateRecord.employeeOfferId);
      setReactivateRecord(null);
      await load();
    } catch {
      setError('Failed to reactivate offer');
    } finally {
      setReactivating(false);
    }
  };

  return (
    <div>
      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}

      <Form onSubmit={handleSearch} className="d-flex gap-2 mb-3">
        <InputGroup style={{ maxWidth: 380 }}>
          <Form.Control
            placeholder="Search name, email, ID, or account…"
            value={searchInput}
            onChange={e => setSearchInput(e.target.value)}
          />
          <Button variant="outline-secondary" type="submit"><FaSearch /></Button>
        </InputGroup>
      </Form>

      {loading ? (
        <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
      ) : (
        <>
          <div className="table-responsive">
            <Table hover size="sm" className="eo-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Employee ID</th>
                  <th>Business Agreement</th>
                  <th>Start Date</th>
                  <th>Deactivated</th>
                  <th style={{ width: 80 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {data?.items.map(r => (
                  <tr key={r.employeeOfferId}>
                    <td>{r.employeeFirstName} {r.employeeLastName}</td>
                    <td className="text-truncate" style={{ maxWidth: 200 }}>{r.employeeEmail}</td>
                    <td>{r.employeeId}</td>
                    <td>{r.businessAgreement}</td>
                    <td>{fmtDate(r.employeeStartDate)}</td>
                    <td className="text-muted">{fmtDate(r.effEndDt)}</td>
                    <td>
                      <Button size="sm" variant="outline-success" title="Reactivate"
                        onClick={() => setReactivateRecord(r)}>
                        <FaUndo />
                      </Button>
                    </td>
                  </tr>
                ))}
                {data?.items.length === 0 && (
                  <tr><td colSpan={7} className="text-center text-muted py-4">No deactivated records found</td></tr>
                )}
              </tbody>
            </Table>
          </div>
          {data && <PaginationBar page={page} pageSize={PAGE_SIZE} total={data.totalCount} onChange={setPage} />}
        </>
      )}

      {/* Reactivate Confirm */}
      <Modal show={!!reactivateRecord} onHide={() => setReactivateRecord(null)}>
        <Modal.Header closeButton>
          <Modal.Title>Reactivate Offer</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Reactivate the offer for{' '}
          <strong>{reactivateRecord?.employeeFirstName} {reactivateRecord?.employeeLastName}</strong>?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setReactivateRecord(null)}>Cancel</Button>
          <Button variant="success" onClick={handleReactivate} disabled={reactivating}>
            {reactivating ? <Spinner size="sm" animation="border" /> : 'Reactivate'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}

// ─── Sync Runs Tab ────────────────────────────────────────────────────────────────

const STAT_KEYS = ['rechecked','deactivated','synch','matched','inserted','updated','contractors','notFound','markedImported'] as const;
const STAT_LABELS: Record<string, string> = {
  rechecked: 'Rechecked', deactivated: 'Deactivated', synch: 'Queued',
  matched: 'Matched', inserted: 'Inserted', updated: 'Updated',
  contractors: 'Contractors', notFound: 'Not Found', markedImported: 'Imported',
};
const STAT_VARIANTS: Record<string, string> = {
  rechecked: 'secondary', deactivated: 'danger', synch: 'primary',
  matched: 'success', inserted: 'success', updated: 'info',
  contractors: 'warning', notFound: 'danger', markedImported: 'success',
};

function parseRunStats(reportResult?: string): Record<string, number> | null {
  if (!reportResult) return null;
  try {
    const parsed = JSON.parse(reportResult);
    const msg: string = parsed.message ?? '';
    const stats: Record<string, number> = {};
    for (const [, key, val] of msg.matchAll(/(\w+)=(\d+)/g)) {
      stats[key] = parseInt(val, 10);
    }
    return Object.keys(stats).length ? stats : null;
  } catch {
    return null;
  }
}

function runDuration(start?: string, end?: string): string {
  if (!start || !end) return '—';
  const secs = Math.round((new Date(end).getTime() - new Date(start).getTime()) / 1000);
  if (secs < 60) return `${secs}s`;
  return `${Math.floor(secs / 60)}m ${secs % 60}s`;
}

function statusVariant(s: string) {
  if (s === 'COMPLETE') return 'success';
  if (s === 'ERROR') return 'danger';
  if (s === 'RUNNING') return 'primary';
  return 'secondary';
}

function SyncRunsTab() {
  const [data, setData] = useState<PagedResult<SyncReportRun> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [expanded, setExpanded] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      setData(await getReportRuns(page, PAGE_SIZE));
    } catch {
      setError('Failed to load sync runs');
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
      {loading ? (
        <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
      ) : (
        <>
          <div className="table-responsive">
            <Table hover size="sm" className="eo-table">
              <thead>
                <tr>
                  <th style={{ width: 170 }}>Run Date</th>
                  <th style={{ width: 110 }}>Status</th>
                  <th style={{ width: 90 }}>Duration</th>
                  <th>Stats</th>
                  <th style={{ width: 80 }}>Report</th>
                </tr>
              </thead>
              <tbody>
                {data?.items.map(r => {
                  const stats = parseRunStats(r.reportResult);
                  const isOpen = expanded === r.m365ReportRunId;
                  return (
                    <>
                      <tr
                        key={r.m365ReportRunId}
                        className={isOpen ? 'table-active' : ''}
                        style={{ cursor: stats ? 'pointer' : undefined }}
                        onClick={() => setExpanded(isOpen ? null : r.m365ReportRunId)}
                      >
                        <td className="text-nowrap">{fmt(r.createDt)}</td>
                        <td><Badge bg={statusVariant(r.reportStatus)}>{r.reportStatus}</Badge></td>
                        <td className="text-muted">{runDuration(r.reportStartDate, r.reportEndDate)}</td>
                        <td>
                          {stats ? (
                            <div className="d-flex flex-wrap gap-1">
                              {STAT_KEYS.filter(k => (stats[k] ?? 0) > 0).map(k => (
                                <Badge key={k} bg={STAT_VARIANTS[k]} text={STAT_VARIANTS[k] === 'warning' ? 'dark' : undefined}>
                                  {STAT_LABELS[k]}: {stats[k]}
                                </Badge>
                              ))}
                            </div>
                          ) : (
                            <span className="text-muted small">{r.reportStatus === 'ERROR' ? 'See details below' : '—'}</span>
                          )}
                        </td>
                        <td>
                          {r.reportLink ? (
                            <a href={r.reportLink} target="_blank" rel="noreferrer"
                              onClick={e => e.stopPropagation()}
                              className="btn btn-outline-primary btn-sm">
                              View
                            </a>
                          ) : <span className="text-muted">—</span>}
                        </td>
                      </tr>
                      {isOpen && r.reportResult && (
                        <tr key={`${r.m365ReportRunId}-detail`}>
                          <td colSpan={5}>
                            <div className="eo-run-detail">
                              {stats && (
                                <Row className="g-2 mb-2">
                                  {STAT_KEYS.map(k => (
                                    <Col key={k} xs={6} sm={4} md={3} lg={2}>
                                      <div className="eo-run-stat">
                                        <div className="eo-run-stat-label">{STAT_LABELS[k]}</div>
                                        <div className={`eo-run-stat-value text-${STAT_VARIANTS[k] === 'secondary' ? 'muted' : STAT_VARIANTS[k]}`}>
                                          {stats[k] ?? 0}
                                        </div>
                                      </div>
                                    </Col>
                                  ))}
                                </Row>
                              )}
                              {r.reportStatus === 'ERROR' && (
                                <pre className="eo-run-raw">{r.reportResult}</pre>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  );
                })}
                {data?.items.length === 0 && (
                  <tr><td colSpan={5} className="text-center text-muted py-4">No sync runs found</td></tr>
                )}
              </tbody>
            </Table>
          </div>
          {data && <PaginationBar page={page} pageSize={PAGE_SIZE} total={data.totalCount} onChange={setPage} />}
        </>
      )}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

type TabKey = 'overview' | 'offers' | 'deactivated' | 'queue' | 'contractors' | 'runs' | 'logs';

const EmployeeOfferManagement: React.FC = () => {
  const [tab, setTab] = useState<TabKey>('overview');
  const [dashboard, setDashboard] = useState<EmployeeOfferDashboard | null>(null);
  const [dashLoading, setDashLoading] = useState(true);

  const loadDashboard = useCallback(async () => {
    setDashLoading(true);
    try {
      const d = await getDashboard();
      setDashboard(d);
    } finally {
      setDashLoading(false);
    }
  }, []);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);

  // Refresh KPIs whenever user returns to overview or takes an action
  const handleTabSelect = (k: TabKey) => {
    setTab(k);
    if (k === 'overview') loadDashboard();
  };

  return (
    <Container fluid className="eo-management p-4">
      <div className="mb-4 d-flex justify-content-between align-items-start flex-wrap gap-2">
        <div>
          <h4 className="mb-0">Employee Offer Management</h4>
          <p className="text-muted mb-0">Review and manage offer registrations, sync queue, and import history.</p>
        </div>
        <Button variant="outline-secondary" size="sm" onClick={loadDashboard} title="Refresh KPIs">
          Refresh
        </Button>
      </div>

      {/* KPI bar — always visible */}
      <Row className="g-3 mb-4">
        {dashLoading ? (
          <Col><Spinner animation="border" variant="primary" size="sm" /></Col>
        ) : dashboard ? (
          <>
            <KpiCard label="Active Offers"          value={dashboard.activeOffers}          variant="success" icon={<FaUserCheck />}         onClick={() => handleTabSelect('offers')} />
            <KpiCard label="Pending Sync"           value={dashboard.pendingSync}           variant="primary" icon={<FaInbox />}            onClick={() => handleTabSelect('queue')} />
            <KpiCard label="Contractor Review"      value={dashboard.contractorReview}      variant="warning" icon={<FaHardHat />}          onClick={() => handleTabSelect('contractors')} />
            <KpiCard label="Not Found"              value={dashboard.notFound}              variant="danger"  icon={<FaExclamationTriangle />} onClick={() => handleTabSelect('queue')} />
            <KpiCard label="Deactivated (30 days)"  value={dashboard.deactivatedThisMonth}  variant="muted"   icon={<FaTimesCircle />}       onClick={() => handleTabSelect('deactivated')} />
          </>
        ) : null}
      </Row>

      {/* Tabs */}
      <Nav variant="tabs" activeKey={tab} onSelect={k => handleTabSelect(k as TabKey)} className="eo-tabs mb-3">
        <Nav.Item>
          <Nav.Link eventKey="overview">
            <FaClipboardList className="me-1" /> Overview
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="offers">
            <FaBriefcase className="me-1" /> Active Offers
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="queue">
            <FaInbox className="me-1" /> Sync Queue
            {(dashboard?.pendingSync ?? 0) > 0 && (
              <Badge bg="primary" className="ms-1">{dashboard!.pendingSync}</Badge>
            )}
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="contractors">
            <FaHardHat className="me-1" /> Contractor Review
            {(dashboard?.contractorReview ?? 0) > 0 && (
              <Badge bg="warning" text="dark" className="ms-1">{dashboard!.contractorReview}</Badge>
            )}
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="deactivated">
            <FaBan className="me-1" /> Deactivated
            {(dashboard?.deactivatedThisMonth ?? 0) > 0 && (
              <Badge bg="secondary" className="ms-1">{dashboard!.deactivatedThisMonth}</Badge>
            )}
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="runs">
            <FaHistory className="me-1" /> Sync Runs
          </Nav.Link>
        </Nav.Item>
        <Nav.Item>
          <Nav.Link eventKey="logs">
            <FaClipboardList className="me-1" /> Import Logs
          </Nav.Link>
        </Nav.Item>
      </Nav>

      <div className="eo-tab-content">
        {tab === 'overview'     && <OverviewTab dashboard={dashboard} loading={dashLoading} />}
        {tab === 'offers'       && <ActiveOffersTab />}
        {tab === 'deactivated'  && <DeactivatedOffersTab />}
        {tab === 'queue'        && <SynchQueueTab reviewStatus="Pending" />}
        {tab === 'contractors'  && <SynchQueueTab reviewStatus="ContractorReview" />}
        {tab === 'runs'         && <SyncRunsTab />}
        {tab === 'logs'         && <ImportLogsTab />}
      </div>
    </Container>
  );
};

export default EmployeeOfferManagement;
